import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, createBlock as _createBlock, withDirectives as _withDirectives, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, vShow as _vShow, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  role: "menu",
  class: "sidemenu-panel-container"
}
const _hoisted_2 = {
  key: 0,
  class: "sidemenu-gutter-handle"
}

import { ref, computed, useSlots, isVNode, nextTick, onBeforeMount, onUnmounted, onMounted } from "vue";

import Button from 'primevue/button';
import SideMenuPanel from "./SideMenuPanel.vue";

export type MenuPosition = "right" | "left";
export type HighlightType = "full" | "shadow" | "line";

export interface Props {
    style?: any;
    expanded?: boolean,
    position?: MenuPosition;
    highlight?: HighlightType;
    showLabel?: boolean;
    showTooltip?: boolean;
    staticSize?: boolean;
    initialWidth?: string;
    resizable?: boolean;
    maximized?: boolean;
}

const AUTO_CLOSE_MARGIN = 50;
const MINIMIZE_INDICATION_WIDTH = 4;


export default /*@__PURE__*/_defineComponent({
  __name: 'SideMenu',
  props: {
    style: { default: {} },
    expanded: { type: Boolean, default: true },
    position: { default: "right" },
    highlight: { default: "full" },
    showLabel: { type: Boolean, default: false },
    showTooltip: { type: Boolean, default: true },
    staticSize: { type: Boolean, default: false },
    initialWidth: { default: '100%' },
    resizable: { type: Boolean, default: true },
    maximized: { type: Boolean, default: false }
  },
  emits: [
    "panel-hide",
    "panel-show",
],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const slots = useSlots();

const selectedTabIndex = ref(props.expanded ? 0 : -1);
const panelWidth = ref<number|null>(null);
const listeners = ref({
    move: null,
    end: null,
});
const dragStartPos = ref<number|null>(null);
const dragStartWidth = ref<number|null>(null);
const dragDistance = ref<number|null>(null);
const panelRef = ref(null);
const menuRef = ref(null);
const gutterRef = ref(null);
const minimizeIndicator = ref<boolean>(false);
const resizeObserver = ref<ResizeObserver>();
const resizeHistory = ref<DOMRectReadOnly>();

// const minWidth = () => {
//     return gutterRef.value.clientWidth + menuRef.value.clientWidth + AUTO_CLOSE_MARGIN;

// }

var minWidth: number;
var maxWidth: number;
var menuWidth: number;
var closedWidth: number;

const expanded = computed(() => {
    const res = selectedTabIndex.value > -1;
    return res
});

const panels = computed(() => {
    return slots.default().filter((item) => {
        return (isVNode(item) && item.type === SideMenuPanel);
    });
});

const isStatic = computed(() => (props.staticSize || (expanded.value && panelWidth.value === null)))

const containerStyle = computed(() => {
    if (expanded.value) {
        return {
            ...props.style,
            width: (panelWidth.value !== null ? `${panelWidth.value}px` : props.initialWidth)
        };
    }
    else {
        return {
            ...props.style,
        };
    }
});

const toolTipArgs = computed(() => {
    return {position: ({left: "right", right: "left"}[props.position])};
});

const startDrag = (evt: MouseEvent) => {
    if (listeners.value.move !== null) {
        try {
            document.removeEventListener("mousemove", listeners.value.move);
        }
        catch (e) {null}
        listeners.value.move = null;
    }
    if (listeners.value.end !== null) {
        try {
            document.removeEventListener("mouseup", listeners.value.end);
        }
        catch (e) {null}
        listeners.value.end = null;
    }
    listeners.value.move = document.addEventListener("mousemove", moveDrag);
    listeners.value.end = document.addEventListener("mouseup", endDrag);

    dragStartPos.value = evt.x;
    dragStartWidth.value = panelWidth.value || panelRef.value.clientWidth;
    menuWidth = gutterRef.value.clientWidth + menuRef.value.clientWidth;
    closedWidth = menuWidth + MINIMIZE_INDICATION_WIDTH;
    minWidth = menuWidth + AUTO_CLOSE_MARGIN;
    maxWidth = window.innerWidth * 0.70;
};

const moveDrag = (evt: MouseEvent) => {
    let width: number;
    let resize = true;
    dragDistance.value = evt.x - dragStartPos.value;
    if (props.position === "left") {
        width = dragStartWidth.value + dragDistance.value;
    }
    else {
        width = dragStartWidth.value - dragDistance.value;
    }
    if (width <= minWidth) {
        resize = false;
        // Will be minimizing
        if (panelWidth.value != closedWidth) {
            resize = true;
            width = closedWidth;
            minimizeIndicator.value = true;
        }
    }
    else {
        minimizeIndicator.value = false;
    }
    if (width >= maxWidth) {
        resize = false;
        if (panelWidth.value != maxWidth) {
            width = maxWidth
            resize = true;
        }
    }

    if (resize) {
        panelWidth.value = width;
    }


}

const endDrag = (evt: MouseEvent) => {
    document.removeEventListener("mousemove", moveDrag);
    document.removeEventListener("mouseup", endDrag);
    // If dragged closed, assume they want the panel to be hidden
    if (panelWidth.value <= minWidth) {
        selectedTabIndex.value = -1;
        panelWidth.value = null;
    }
    dragStartPos.value = null;
    dragDistance.value = null;
    minimizeIndicator.value = false;

};

const handleButtonClick = (index: number) => {
    if (selectedTabIndex.value !== index) {
        selectedTabIndex.value = index;
        emit("panel-show");
    }
    else {
        selectedTabIndex.value = -1;
        emit("panel-hide");
    }
}

const selectPanel = (label: string) => {
    selectedTabIndex.value = panels.value.findIndex((panel) => (panel.props?.label === label));
    if (minimizeIndicator.value) {
        minimizeIndicator.value = false;
    }
}

onMounted(() => {
    const target: HTMLElement = document.body;
    // Observer to smoothly resize the side menu when the document size changes (maximizing/resizing a browser window)
    resizeObserver.value = new ResizeObserver((entries) => {
        for (const entry of entries) {
            if (!resizeHistory.value) {
                resizeHistory.value = entry.contentRect;

            }
            else if (entry.contentRect !== resizeHistory.value) {
                if (panelWidth.value) {
                    const resizeRatio = entry.contentRect.width / resizeHistory.value.width;
                    panelWidth.value = panelWidth.value * resizeRatio;
                }
                resizeHistory.value = entry.contentRect;
            }
        }

    });
    resizeObserver.value.observe(target);
})

onUnmounted(() => {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
})

__expose({
    selectPanel,
});


return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["sidemenu-container", [_ctx.position]]),
    style: _normalizeStyle({minWidth: (_ctx.initialWidth && !_ctx.maximized ? _ctx.initialWidth : undefined)})
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["sidemenu", [_ctx.position, (minimizeIndicator.value ? 'minimize' : undefined)]]),
      style: _normalizeStyle(containerStyle.value),
      ref_key: "panelRef",
      ref: panelRef
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["sidemenu-menu-selection", [_ctx.position]]),
        ref_key: "menuRef",
        ref: menuRef
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(panels.value, (panel, index) => {
          return _withDirectives((_openBlock(), _createBlock(_unref(Button), {
            key: `button-${index}`,
            class: _normalizeClass(['menu-button', props.highlight, props.position, (selectedTabIndex.value === index ? 'selected' : undefined), (props.showLabel ? 'show-label' : undefined)]),
            icon: panel.props.icon,
            label: props.showLabel ? panel.props.label : undefined,
            "icon-pos": "top",
            onClick: ($event: any) => (handleButtonClick(index)),
            text: ""
          }, null, 8, ["class", "icon", "label", "onClick"])), [
            [_directive_tooltip, props.showTooltip ? panel.props.label : undefined, toolTipArgs.value]
          ])
        }), 128))
      ], 2),
      _withDirectives(_createElementVNode("div", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(panels.value, (panel, index) => {
          return _withDirectives((_openBlock(), _createBlock(_resolveDynamicComponent(panel), {
            key: `panel-${index}`
          })), [
            [_vShow, selectedTabIndex.value === index]
          ])
        }), 128))
      ], 512), [
        [_vShow, expanded.value]
      ]),
      (expanded.value && !_ctx.staticSize)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "sidemenu-gutter",
            onMousedown: _withModifiers(startDrag, ["prevent"]),
            ref_key: "gutterRef",
            ref: gutterRef
          }, [
            (!_ctx.staticSize)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2))
              : _createCommentVNode("", true)
          ], 544))
        : _createCommentVNode("", true)
    ], 6),
    _cache[0] || (_cache[0] = _createElementVNode("div", { class: "spacer" }, null, -1))
  ], 6))
}
}

})